import React, {useCallback, useEffect, useState} from 'react';
import {FormControl, FormHelperText, Input, InputLabel} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import {AddCircleOutline, RemoveCircleOutline} from "@mui/icons-material";

export interface NumberInputProps {
    onChange: Function;
    label: string;
    defaultValue?: number;
    minValue?: number;
    maxValue?: number;
    disabled?: boolean;
    error?: boolean;
    helperText?: string;
    acceptDecimal?: boolean;
    handleComma?: boolean;
}

/**
 * This component is used to display a number input field
 * @param onChange You have to pass your function to use the value of component
 * Ex. if you use Formik your onChange function have been this : onChange={(value:number) => {setFieldValue('fieldName', value)}}
 * @param label
 * @param defaultValue
 * @param minValue
 * @param maxValue
 * @param disabled
 * A full example of import can be this:
 * <NumberInput onChange={(value:number) => {meta.setFieldValue('age', value)}} defaultValue={18} minValue={18} maxValue={85}/>
 * @param props
 */

const NumberInput = (props: NumberInputProps) => {
    const [currentValue, setCurrentValue] = useState<any>((props.defaultValue !== undefined && !isNaN(props.defaultValue)) ? props.defaultValue :
        (props.minValue !== undefined && !isNaN(props.minValue)) ? props.minValue : 0);
    const [addBtnIsDisabled, setAddBtnIsDisabled] = useState((props.disabled !== undefined) ? props.disabled : false);
    const [removeBtnIsDisabled, setRemoveBtnIsDisabled] = useState((props.disabled !== undefined) ? props.disabled : false);
    const [acceptDecimal] = useState((props.acceptDecimal !== undefined) ? props.acceptDecimal : false);
    const [handleComma] = useState((props.handleComma !== undefined) ? props.handleComma : false);

    const handleSetValue = (operator: string) => {
        let newValue = currentValue;
        let isDecimal = handleComma ?
                            currentValue.toString().includes(',') :
                                currentValue.toString().includes('.');
        if (operator === '+') {
            if (isDecimal){
                if(handleComma){
                    newValue = currentValue.includes(',') ?
                        (Number(currentValue.replaceAll(",", ".")) + 0.01).toFixed(2) :
                        (Number(currentValue) - 0.01).toFixed(2);
                    if (newValue.toString().split('.')[1] === '00') {
                        newValue = Math.floor(newValue);
                    } else {
                        newValue = newValue.replaceAll(".", ",")
                    }
                } else {
                    newValue = (Number(currentValue) + 0.01).toFixed(2);
                    if (newValue.toString().split('.')[1] === '00') {
                        newValue = Math.floor(newValue);
                    }
                }
            } else {
                newValue = Number(currentValue) + 1;
            }
        } else if (operator === '-') {
            if (isDecimal){
                if(handleComma){
                    newValue = currentValue.includes(',') ?
                                    (Number(currentValue.replaceAll(",", ".")) - 0.01).toFixed(2) :
                                    (Number(currentValue) - 0.01).toFixed(2);
                    if (newValue.toString().split('.')[1] === '00') {
                        newValue = Math.floor(newValue);
                    } else {
                        newValue = newValue.replaceAll(".", ",")
                    }
                } else {
                    newValue = (Number(currentValue) - 0.01).toFixed(2);
                    if (newValue.toString().split('.')[1] === '00') {
                        newValue = Math.floor(newValue);
                    }
                }
            } else {
                newValue = Number(currentValue) - 1;
            }
        }
        setCurrentValue(newValue);
    }
    const handleClickAdd = () => {
        if (props.maxValue !== undefined && !isNaN(props.maxValue)) {
            if (currentValue < props.maxValue) {
                handleSetValue('+');
            }
        } else {
            handleSetValue('+');
        }
    }

    const handleClickRemove = () => {
        if (props.minValue !== undefined && !isNaN(props.minValue)) {
            if (currentValue > props.minValue) {
                handleSetValue('-');
            }
        } else {
            handleSetValue('-');
        }
    }

    const isTextSelected = (input:any) => {
        let selecttxt: any = '';
        if (window.getSelection) {
            selecttxt = window.getSelection();
        } else if (document.getSelection) {
            selecttxt = document.getSelection();
            // @ts-ignore
        } else if (document?.selection) {
            // @ts-ignore
            selecttxt = document?.selection.createRange().text;
        }

        if (selecttxt == '') {
            return false;
        }
        return true;

    }

    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        if (e.key === 'ArrowUp') {
            handleClickAdd();
        } else if (e.key === 'ArrowDown') {
            handleClickRemove();
        } else if (e.key === 'Backspace') {
            let valueTmp = Number(currentValue.toString().slice(0, -1));
            if (!isNaN(valueTmp) && props.minValue !== undefined && !isNaN(props.minValue) && valueTmp < props.minValue) {
                setCurrentValue(props.minValue);
            } else {
                setCurrentValue(isNaN(valueTmp) ? 0 : valueTmp);
            }
        } else if (/\d/.test(e.key)) {
            let valueTmp = isTextSelected(e.currentTarget) ? Number(e.key) :
                                         handleComma? Number((currentValue.toString() + e.key).replaceAll(",", ".")) : Number(currentValue.toString() + e.key);
            if (!isNaN(valueTmp)) {
                if (!isTextSelected(e.currentTarget) && (e.currentTarget.value.includes('.') || e.currentTarget.value.includes(','))) {
                    let decimalSeparator = e.currentTarget.value.includes('.') ? '.' : ',';
                    let decimalPlaces = e.currentTarget.value.split(decimalSeparator)[1]?.length || 0;
                    let newValue = e.currentTarget.value + e.key;
                    if (decimalPlaces < 2) {
                        setCurrentValue(newValue);
                    }
                } else {
                    setCurrentValue(valueTmp);
                }
            }
        } else if (acceptDecimal && (e.key === '.' || e.key === ',')) {
            if (!e.currentTarget.value.includes('.') && !e.currentTarget.value.includes(',')) {
                handleComma ? setCurrentValue(currentValue.toString() + ',') : setCurrentValue(currentValue.toString() + '.');
            }
        }
    }, [handleClickRemove, handleClickAdd]);


    useEffect(() => {
        if (props.minValue !== undefined && !isNaN(props.minValue) && props.minValue === currentValue) {
            setRemoveBtnIsDisabled(true);
        } else if (props.disabled !== undefined && props.disabled) {
            setRemoveBtnIsDisabled(true);
        } else {
            setRemoveBtnIsDisabled(false);
        }
        if (props.maxValue !== undefined && !isNaN(props.maxValue) && props.maxValue === currentValue) {
            setAddBtnIsDisabled(true);
        } else if (props.disabled !== undefined && props.disabled) {
            setAddBtnIsDisabled(true);
        } else {
            setAddBtnIsDisabled(false);
        }
        props.onChange(currentValue);
    }, [currentValue])

    useEffect(() => {
        if (props.minValue !== undefined && !isNaN(props.minValue) && props.minValue === currentValue) {
            setRemoveBtnIsDisabled(true);
        } else if (props.disabled !== undefined && props.disabled) {
            setRemoveBtnIsDisabled(true);
        } else {
            setRemoveBtnIsDisabled(false);
        }
        if (props.maxValue !== undefined && !isNaN(props.maxValue) && props.maxValue === currentValue) {
            setAddBtnIsDisabled(true);
        } else if (props.disabled !== undefined && props.disabled) {
            setAddBtnIsDisabled(true);
        } else {
            setAddBtnIsDisabled(false);
        }
    }, [props.minValue, props.maxValue, props.disabled])

    useEffect(() => {
        if(handleComma){
            if (props.defaultValue !== undefined) {
                setCurrentValue(props.defaultValue);
            }
        } else {
            if (props.defaultValue !== undefined && !isNaN(props.defaultValue)) {
                setCurrentValue(props.defaultValue);
            }
        }
    }, [props.defaultValue]);

    return (
        <FormControl fullWidth
                     sx={{
                         height: '100%',
                         justifyContent: 'center',
                         marginBottom: '0px',
                         '& .MuiInputLabel-root': {
                             color: '#011e41',
                             left: '-8px',
                         },
                         '& .MuiInputBase-root': {
                             marginTop: '8px',
                         }
                     }}>
            {props.error ? <InputLabel error>{props.label}</InputLabel> : <InputLabel>{props.label}</InputLabel>}
            <Input
                inputProps={{
                    style: {
                        textAlign: 'center'
                    },
                }}
                startAdornment={
                    <IconButton disabled={removeBtnIsDisabled}
                                onClick={handleClickRemove}><RemoveCircleOutline/></IconButton>
                }
                endAdornment={
                    <IconButton disabled={addBtnIsDisabled} onClick={handleClickAdd}><AddCircleOutline/></IconButton>
                }
                value={currentValue}
                disabled={props.disabled}
                onKeyDown={(e) => handleKeyDown(e)}
                error={props.error}
            />
            {props.error ? <FormHelperText error>{props.helperText}</FormHelperText> :
                <FormHelperText>{props.helperText}</FormHelperText>}
        </FormControl>
    );
};

export default NumberInput;
